/* eslint-disable max-lines-per-function */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import classNames from 'classnames';

import { catalogueStaticLinks } from 'apps/catalogue/links';

import { links } from '[theme-lib]/links';

import BrowseShop from 'apps/navigation/containers/BrowseShop';
import PrimaryBarDropdown from 'apps/header/components/PrimaryBarDropdown';
import Icon from 'lib/widgets/Icon/Icon';

import TilesDropdown from 'apps/ospHeader/components/TilesDropdown';
import { gtm } from 'apps/gtm';

const PrimaryBar = ({ isLoggedIn, easyShopping = {}, inspireMe = {}, offers = {} }) => {
  const primaryBarRef = useRef(null);

  const sendAnalyticsEvent = (data) => {
    gtm.trigger('gtm:navigationEvent', data);
  };

  const shouldShowTilesDropdown = (dropdown) => {
    const hasTiles = dropdown.tiles && dropdown.tiles.length > 0;
    const hasLinks = dropdown.links && dropdown.links.length > 0;

    return hasTiles || hasLinks;
  };

  const getDropdownLabel = (text) => (
    <span>
        { text }
      <Icon
        name="chevron-down"
        className="hd-header__primaryBarChevronDown"
      />
    </span>
  );

  const getItemWithTilesDropdown = ({
    className, defaultBtnText, dropdown, fallback, label, link, itemsLimit,
  }) => {
    if (fallback) {
      return (
        <li className="hd-header__primaryBarItem">
          <Link
            className="primaryBar__link"
            to={link}
            onClick={() => sendAnalyticsEvent(label)}
          >
            {label}
          </Link>
        </li>
      );
    }

    return (
      <li className={`hd-header__primaryBarItem with-dropdown ${className}`}>
        <PrimaryBarDropdown
          label={getDropdownLabel(label)}
          rawLabel={label}
          href={link}
          analyticsEventSender={sendAnalyticsEvent}
        >
          <TilesDropdown
            defaultBtnText={defaultBtnText}
            dropdown={dropdown}
            dropdownLabel={label}
            primaryBarRef={primaryBarRef}
            itemsLimit={itemsLimit}
            analyticsEventSender={sendAnalyticsEvent}
          />
        </PrimaryBarDropdown>
      </li>
    );
  };

  const hasInspireMe = shouldShowTilesDropdown(inspireMe);
  const hasEasyShopping = shouldShowTilesDropdown(easyShopping);

  // Items limit in tile dropdown depends on the primary bar items count
  const staticItemsCount = 4;
  const itemsLimit = staticItemsCount + hasInspireMe + hasEasyShopping;

  return (
    <div className={classNames('hd-header__primaryBar', { logged: isLoggedIn })}>
      <ol
        id="main-navigation"
        className="hd-header__navCommon"
        ref={primaryBarRef}
      >
        <li className="hd-header__primaryBarItem with-dropdown small-left-pad browse-shop">
          <PrimaryBarDropdown
            label={getDropdownLabel('Browse Shop')}
            rawLabel={'Browse Shop'}
            href={catalogueStaticLinks.BROWSE}
            analyticsEventSender={sendAnalyticsEvent}
          >
            <BrowseShop />
          </PrimaryBarDropdown>
        </li>
        <li className="hd-header__primaryBarItem">
          <Link
            className="primaryBar__link"
            to={links.SHOW_FAVOURITES}
            onClick={() => sendAnalyticsEvent('Favourites')}
          >
            Favourites
          </Link>
        </li>
        { getItemWithTilesDropdown({
          className: 'offers',
          defaultBtnText: 'Shop Now',
          dropdown: offers,
          fallback: !shouldShowTilesDropdown(offers),
          label: 'Offers',
          link: catalogueStaticLinks.OFFERS,
          itemsLimit,
        })}
        { hasInspireMe && getItemWithTilesDropdown({
          className: 'seasonal',
          defaultBtnText: 'Shop Now',
          dropdown: inspireMe,
          label: 'Inspire Me',
          itemsLimit,
        })}
        { hasEasyShopping && getItemWithTilesDropdown({
          className: 'easy-shopping',
          dropdown: easyShopping,
          label: 'Easy Shopping',
          itemsLimit,
        })}
        <li className="hd-header__primaryBarItem">
          <Link
            className="primaryBar__link"
            to="/webshop/displayDeliveryPass.do"
            onClick={() => sendAnalyticsEvent('Smart Pass')}
          >
            Smart Pass
          </Link>
        </li>
      </ol>
    </div>
  );
};

PrimaryBar.propTypes = {
  easyShopping: PropTypes.shape({
    links: PropTypes.array,
    tiles: PropTypes.array,
  }),
  isLoggedIn: PropTypes.bool.isRequired,
  inspireMe: PropTypes.shape({
    links: PropTypes.array,
    tiles: PropTypes.array,
  }),
  offers: PropTypes.shape({
    links: PropTypes.array,
    tiles: PropTypes.array,
  }),
};

export default PrimaryBar;
